/**
 * ChatPracticePreview — CORE-353.
 *
 * Restores the practice link-preview that GetStream used to render via og:video
 * enrichment, now that AI/DM chat runs on Agape.ChatApi (which doesn't enrich
 * links). Given a practice id parsed from the message text, fetch the practice's
 * own cover + playback URL and render a LIGHTWEIGHT poster + play badge — no
 * `<video>` is mounted in the message list (that was the ~1.4s INP jank). On
 * click (web) the real player opens in a portal lightbox at document.body, so
 * playback survives message-list re-renders and fullscreen works (the same
 * lesson as the Stream-side AgapellaAttachment: never play inline in the
 * virtualized/transformed list). HLS (`.m3u8`) is handled via hls.js with a
 * Safari-native fallback, mirroring DirectVideoPlayer.
 *
 * Web-first: native renders a static poster+title card (chat is web-gated).
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, Image, Pressable, Platform, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { api, getVideoUrl, getFileUrl } from '../../services/api';
import type { VideoPracticeDetail } from '../../types/auth';
import { colors, radius, spacing, typography, shadows } from '../../styles';
import { useTheme } from '../../context/ThemeContext';

const isWeb = Platform.OS === 'web';

// Session cache: one fetch per practice id, shared across every message that
// links it (and across re-renders). Stores the in-flight promise so concurrent
// messages don't double-fetch. `null` = fetched but not previewable.
const cache = new Map<number, Promise<VideoPracticeDetail | null>>();

function loadPractice(id: number): Promise<VideoPracticeDetail | null> {
  let p = cache.get(id);
  if (!p) {
    p = api
      .getVideoPracticeDetail(id)
      .catch(() => null);
    cache.set(id, p);
  }
  return p;
}

/** Web-only portal lightbox that plays an MP4/HLS url full-screen. */
function VideoLightbox({ url, poster, onClose }: { url: string; poster?: string | null; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<any>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    const isHls = url.endsWith('.m3u8');
    // CORE-451 — `=== 'probably'`, not truthiness: Chrome/Firefox answer
    // `'maybe'` for the HLS mime type without being able to play it, so a
    // truthy test silently skipped hls.js and the video never started.
    const playsHlsNatively = video.canPlayType('application/vnd.apple.mpegurl') === 'probably';
    if (isHls && !playsHlsNatively) {
      // Chrome/Firefox need hls.js; Safari plays HLS natively (branch above).
      import('hls.js').then(({ default: Hls }) => {
        if (cancelled) return;
        if (!Hls.isSupported()) { video.src = url; return; }
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      });
    } else {
      video.src = url;
    }
    return () => {
      cancelled = true;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      video.pause(); video.removeAttribute('src'); video.load();
    };
  }, [url]);

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <video
        ref={videoRef}
        poster={poster || undefined}
        controls
        autoPlay
        playsInline
        preload="metadata"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '86vh', width: 'auto', height: 'auto', borderRadius: radius.lg, background: '#000' }}
      />
      <button
        type="button"
        onClick={onClose}
        aria-label="Закрыть"
        style={{
          position: 'absolute', top: spacing.base, right: spacing.base,
          width: 44, height: 44, borderRadius: radius.pill, border: 'none',
          background: 'rgba(0,0,0,0.5)', color: colors.white, fontSize: spacing.lg,
          lineHeight: 1, cursor: 'pointer',
        }}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}

export const ChatPracticePreview: React.FC<{ id: number; mine?: boolean }> = ({ id }) => {
  const { isDark } = useTheme();
  const [detail, setDetail] = useState<VideoPracticeDetail | null | undefined>(undefined);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let alive = true;
    loadPractice(id).then((d) => { if (alive) setDetail(d); });
    return () => { alive = false; };
  }, [id]);

  // Still loading, or the practice couldn't be fetched (deleted / private) →
  // render nothing; the plain link stays visible in the message text.
  if (!detail) return null;

  const poster = getFileUrl(detail.coverImageUrl) || undefined;
  const videoUrl = getVideoUrl(detail);
  const s = makeStyles(isDark);
  const minutes = detail.durationMinutes;

  const card = (
    <View style={s.frame} testID="chat-practice-preview">
      {poster ? (
        <Image source={{ uri: poster }} style={s.poster} resizeMode="cover" />
      ) : (
        <View style={[s.poster, s.posterFallback]} />
      )}
      {videoUrl ? (
        <View style={s.playBadge}>
          <Ionicons name="play" size={20} color={colors.white} style={{ marginLeft: 2 }} />
        </View>
      ) : null}
      <View style={s.meta}>
        <Text style={s.title} numberOfLines={1}>{detail.title}</Text>
        {typeof minutes === 'number' && minutes > 0 ? (
          <Text style={s.sub}>{minutes} мин</Text>
        ) : null}
      </View>
    </View>
  );

  // Native (chat is web-gated, but keep it safe): static card, no lightbox.
  if (!isWeb || !videoUrl) return card;

  return (
    <>
      <Pressable
        onPress={() => setPlaying(true)}
        accessibilityRole="button"
        accessibilityLabel="Смотреть практику"
      >
        {card}
      </Pressable>
      {playing ? <VideoLightbox url={videoUrl} poster={poster} onClose={() => setPlaying(false)} /> : null}
    </>
  );
};

function makeStyles(isDark: boolean) {
  return StyleSheet.create({
    frame: {
      width: 260,
      maxWidth: '100%',
      borderRadius: radius.lg,
      overflow: 'hidden',
      backgroundColor: isDark ? colors.charcoal : colors.surfaceSoft,
      marginBottom: spacing.xs,
      // Web-only soft shadow (RN-web passes boxShadow through); chat is web-gated.
      ...(isWeb ? { boxShadow: shadows.card } : null),
    },
    poster: { width: '100%', aspectRatio: 16 / 9 },
    posterFallback: { backgroundColor: isDark ? '#2a2320' : '#e9e4de' },
    playBadge: {
      position: 'absolute',
      top: '38%',
      left: '50%',
      marginLeft: -22,
      width: 44,
      height: 44,
      borderRadius: radius.pill,
      backgroundColor: colors.solar,
      alignItems: 'center',
      justifyContent: 'center',
    },
    meta: { paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
    title: { fontSize: typography.body.fontSize, fontWeight: '600', color: isDark ? colors.white : colors.charcoal },
    sub: { fontSize: typography.caption.fontSize, color: isDark ? 'rgba(255,255,255,0.6)' : colors.textMuted, marginTop: 2 },
  });
}

export default ChatPracticePreview;
